import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from './Layout';
import { Dashboard } from './Dashboard';
import { ProjectView } from './ProjectView';
import { SettingsView } from './SettingsView';
import { InboxView } from './InboxView';
import { PeopleView } from './PeopleView';
import { generateProjectSlug, findProjectBySlug } from '../utils/routing';
import {
  UserRole,
  Project,
  ProjectStatus,
  MilestoneStatus,
  PresenceStatus,
  Workspace,
  Person,
  Notification
} from '../types';
import { DemoWalkthrough } from './DemoWalkthrough';

// --- MOCK DATA ---

const DEMO_PROVIDER_WORKSPACE: Workspace = {
  id: 'demo-agency-workspace',
  name: 'Demo Provider',
  role: UserRole.PROVIDER,
  initials: 'DA',
};

const DEMO_RECEIVER_WORKSPACE: Workspace = {
  id: 'demo-client-workspace',
  name: 'Acme Corp',
  role: UserRole.RECEIVER,
  initials: 'AC',
};

const DEMO_USER_PROVIDER = {
  id: 'demo-user-provider',
  email: 'founder@demo-provider.com',
  user_metadata: { name: 'Provider Founder' }
};

const DEMO_USER_RECEIVER = {
  id: 'demo-user-receiver',
  email: 'contact@acme.com',
  user_metadata: { name: 'Receiver Contact' }
};

const INITIAL_PROJECTS: Project[] = [
  {
    id: 'p1',
    name: 'Website Redesign',
    receiverName: 'Acme Corp',
    receiverEmail: 'contact@acme.com',
    description: 'Complete overhaul of the corporate website.',
    status: ProjectStatus.ACTIVE,
    milestones: [
      { id: 'm1', title: 'Discovery Phase', status: MilestoneStatus.APPROVED, duration: 5, comments: [], completedAt: new Date().toISOString() },
      { id: 'm2', title: 'Wireframes', status: MilestoneStatus.IN_PROGRESS, duration: 7, comments: [] },
      { id: 'm3', title: 'Visual Design', status: MilestoneStatus.NOT_STARTED, duration: 10, comments: [] }
    ],
    startDate: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    notes: [],
    assigneeVisibility: 'MILESTONE',
    onboardingQuestions: [],
    workspaceId: 'demo-provider-workspace', // Belongs to provider workspace
    files: []
  },
  {
    id: 'p2',
    name: 'Mobile App MVP',
    receiverName: 'Startup Inc',
    receiverEmail: 'founder@startup.io',
    description: 'Initial MVP for the new mobile platform.',
    status: ProjectStatus.ACTIVE,
    milestones: [
      { id: 'm1', title: 'Backend Setup', status: MilestoneStatus.APPROVED, duration: 3, comments: [], completedAt: new Date().toISOString() },
      { id: 'm2', title: 'API development', status: MilestoneStatus.REVIEW, duration: 5, comments: [] },
      { id: 'm3', title: 'Frontend Integration', status: MilestoneStatus.NOT_STARTED, duration: 8, comments: [] }
    ],
    startDate: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    notes: [],
    assigneeVisibility: 'MILESTONE',
    onboardingQuestions: [],
    workspaceId: 'demo-agency-workspace',
    files: []
  }
];

const MOCK_NOTIFICATIONS: Notification[] = [
  { id: 'n1', type: 'system', message: 'New comment on Wireframes', projectId: 'p1', isRead: false, date: new Date().toISOString() },
  { id: 'n2', type: 'approval', message: 'Discovery Phase approved', projectId: 'p1', isRead: true, date: new Date().toISOString() }
];

const MOCK_PEOPLE: Person[] = [
  { id: 'per1', name: 'Alice Smith', email: 'alice@receiver.com', initials: 'AS', avatarColor: 'bg-rose-500', role: 'Collaborator', relationshipStatus: 'Active', sharedProjectsCount: 1, company: 'Acme Corp' },
  { id: 'per2', name: 'Bob Jones', email: 'bob@receiver.com', initials: 'BJ', avatarColor: 'bg-emerald-500', role: 'Collaborator', relationshipStatus: 'Active', sharedProjectsCount: 1, company: 'Startup Inc' },
  { id: 'per3', name: 'Charlie Day', email: 'charlie@receiver.com', initials: 'CD', avatarColor: 'bg-blue-500', role: 'Receiver', relationshipStatus: 'Active', sharedProjectsCount: 1, company: 'Acme Corp' }
];

export const Demo = () => {
  // State
  const [role, setRole] = useState<UserRole>(UserRole.PROVIDER);
  const [currentPath, setCurrentPath] = useState('/agency/dashboard');
  const [projects, setProjects] = useState<Project[]>(INITIAL_PROJECTS);
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);

  // Walkthrough State
  const [hasSeenAgencyWalkthrough, setHasSeenAgencyWalkthrough] = useState(false);
  const [hasSeenClientWalkthrough, setHasSeenClientWalkthrough] = useState(false);

  // Derived State
  const currentUser = role === UserRole.PROVIDER ? DEMO_USER_PROVIDER : DEMO_USER_RECEIVER;
  const currentWorkspace = role === UserRole.PROVIDER ? DEMO_PROVIDER_WORKSPACE : DEMO_RECEIVER_WORKSPACE;

  // Filter Data based on Role
  const filteredProjects = useMemo(() => {
    if (role === UserRole.PROVIDER) return projects;
    return projects.filter(p => p.receiverEmail === DEMO_USER_RECEIVER.email);
  }, [projects, role]);

  const filteredPeople = useMemo(() => {
    if (role === UserRole.PROVIDER) return MOCK_PEOPLE;
    // Receiver sees Provider staff (mocked)
    return [
      { id: 'provider-lead', name: 'Provider Founder', email: 'founder@demo-provider.com', initials: 'PF', avatarColor: 'bg-indigo-500', role: 'Provider', relationshipStatus: 'Active', sharedProjectsCount: 1, company: 'Demo Provider' }
    ] as Person[];
  }, [role]);

  // View determination logic
  const getViewFromPath = (path: string): string => {
    if (path.includes('/dashboard/archive')) return 'archive';
    if (path.includes('/dashboard/inbox')) return 'inbox';
    if (path.includes('/dashboard/people')) return 'people';
    if (path.includes('/dashboard/settings')) return 'settings';
    if (path.includes('/project/')) return 'project-detail';
    return 'projects';
  };

  const currentView = getViewFromPath(currentPath);
  const rolePrefix = role === UserRole.PROVIDER ? '/provider' : '/receiver';

  // --- Handlers ---

  const handleCustomNavigate = (path: string) => {
    setCurrentPath(path);
    // Manage editing state
    if (!path.includes('/projects/')) {
      setEditingProjectId(null);
    }
  };

  const handleSwitchRole = () => {
    const newRole = role === UserRole.PROVIDER ? UserRole.RECEIVER : UserRole.PROVIDER;
    setRole(newRole);
    // Reset path to dashboard of new role to prevent getting stuck in unauthorized views
    const newPrefix = newRole === UserRole.PROVIDER ? '/provider' : '/receiver';
    setCurrentPath(`${newPrefix}/dashboard`);
  };

  const handleSelectProject = (id: string) => {
    const project = filteredProjects.find(p => p.id === id);
    if (project) {
      const slug = generateProjectSlug(project);
      handleCustomNavigate(`${rolePrefix}/project/${slug}`);
    }
  };

  const handleUpdateProject = (projectId: string, data: Partial<Project>) => {
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...data } : p));
  };

  const handleCreateProject = async (newProject: Partial<Project>): Promise<Project | undefined> => {
    const project: Project = {
      id: Math.random().toString(36).substr(2, 9),
      name: newProject.name || 'Untitled',
      receiverName: newProject.receiverName || '',
      receiverEmail: newProject.receiverEmail || '',
      description: newProject.description,
      status: newProject.status || ProjectStatus.ACTIVE,
      milestones: newProject.milestones || [],
      startDate: newProject.startDate || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      notes: [],
      assigneeVisibility: newProject.assigneeVisibility || 'MILESTONE',
      onboardingQuestions: newProject.onboardingQuestions || [],
      workspaceId: 'demo-provider-workspace',
      files: []
    } as Project;

    setProjects([project, ...projects]);
    handleCustomNavigate(`${rolePrefix}/dashboard`);
    return project;
  };

  // --- Render ---

  let content;
  if (currentView === 'project-detail') {
    const slug = currentPath.split('/').pop();
    const project = findProjectBySlug(slug || '', filteredProjects);

    if (project) {
      content = <ProjectView
        project={project}
        role={role}
        teamMembers={[]}
        onBack={() => handleCustomNavigate(`${rolePrefix}/dashboard`)}
        onUpdateMilestone={(pid, mid, status) => {
          // Mock update
          setProjects(prev => prev.map(p => {
            if (p.id !== pid) return p;
            return { ...p, milestones: p.milestones.map(m => m.id === mid ? { ...m, status } : m) }
          }));
        }}
        onAssignMilestone={() => { }}
        onInviteMember={() => 'mock-id'}
        onUpdateProjectStatus={() => { }}
        onUpdateAssigneeVisibility={() => { }}
        onUpdateProjectQuestions={() => { }}
        onUpdateMilestoneTitle={() => { }}
        onAddComment={(pid, mid, text, atts, isDelivery) => {
          // Mock add comment
          const newComment = { id: Math.random().toString(), author: currentUser.user_metadata.name, role, text, date: new Date().toISOString(), isResolved: false, isDelivery, attachments: atts || [] };
          setProjects(prev => prev.map(p => {
            if (p.id !== pid) return p;
            return { ...p, milestones: p.milestones.map(m => m.id === mid ? { ...m, comments: [...m.comments, newComment] } : m) }
          }));
        }}
        onResolveComment={() => { }}
        onDeleteProject={() => handleCustomNavigate(`${rolePrefix}/dashboard`)}
        onArchiveProject={() => handleCustomNavigate(`${rolePrefix}/dashboard`)}
        onRestoreProject={() => { }}
        onOpenReceiverView={() => handleCustomNavigate(`/receiver/project/${slug}`)} // Allow provider to preview receiver view? Or maybe just switch role.
        onDeliverWork={() => { }}
        onApproveMilestone={(pid, mid) => {
          setProjects(prev => prev.map(p => {
            if (p.id !== pid) return p;
            return { ...p, milestones: p.milestones.map(m => m.id === mid ? { ...m, status: MilestoneStatus.APPROVED, completedAt: new Date().toISOString() } : m) }
          }));
        }}
        onAddNote={() => { }}
        onUpdateNote={() => { }}
        onDeleteNote={() => { }}
        onAddProjectFile={() => { }}
        onFinalizeProject={() => { }}
        onUpdateProject={handleUpdateProject}
        onCompleteDraft={() => handleCustomNavigate(`${rolePrefix}/dashboard`)}
      />;
    } else {
      content = <div className="p-10 text-center text-zinc-500">Project not found or you don't have access.</div>;
    }
  } else if (currentView === 'inbox') {
    content = <InboxView notifications={MOCK_NOTIFICATIONS} projects={filteredProjects} onSelect={(n) => handleSelectProject(n.projectId)} onMarkAsRead={() => { }} onMarkAllAsRead={() => { }} />;
  } else if (currentView === 'people') {
    content = <PeopleView people={filteredPeople} role={role} onAddPerson={() => { }} projects={filteredProjects} onSelectProject={handleSelectProject} />;
  } else if (currentView === 'settings') {
    content = <SettingsView role={role} user={{ name: currentUser.user_metadata.name, email: currentUser.email, initials: currentUser.user_metadata.name.substring(0, 2).toUpperCase(), avatarColor: 'bg-zinc-700' }} workspace={currentWorkspace} onWorkspaceDeleted={() => { }} allWorkspaces={[DEMO_PROVIDER_WORKSPACE, DEMO_RECEIVER_WORKSPACE]} />;
  } else {
    // Dashboard
    content = <Dashboard
      projects={filteredProjects}
      onSelectProject={handleSelectProject}
      onCreateProject={handleCreateProject}
      role={role}
      autoOpenCreate={currentPath.includes('/projects/new')}
      teamMembers={[]}
      onBulkDeleteProjects={async () => { }}
      onBulkRestoreProjects={async () => { }}
      editingProject={editingProjectId ? projects.find(p => p.id === editingProjectId) : undefined}
      onUpdateProjectAndSend={async (id, data) => { handleUpdateProject(id, data); return projects.find(p => p.id === id); }}
      onCloseEdit={() => setEditingProjectId(null)}
      customNavigate={handleCustomNavigate}
      customLocation={{ pathname: currentPath }}
    />;
  }

  return (
    <Layout
      currentRole={role}
      currentView={currentView === 'project-detail' ? 'projects' : currentView}
      onNavigate={(view) => {
        if (view === 'projects') handleCustomNavigate(`${rolePrefix}/dashboard`);
        else if (view === 'archive') handleCustomNavigate(`${rolePrefix}/dashboard/archive`);
        else if (view === 'inbox') handleCustomNavigate(`${rolePrefix}/dashboard/inbox`);
        else if (view === 'people') handleCustomNavigate(`${rolePrefix}/dashboard/people`);
        else if (view === 'settings') handleCustomNavigate(`${rolePrefix}/dashboard/settings`);
      }}
      onSwitchRole={handleSwitchRole}
      onLogout={() => { window.location.href = '/'; }}
      currentPresence={PresenceStatus.ACTIVE}
      theme={theme}
      onToggleTheme={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
      userName={currentUser.user_metadata.name}
      userInitial={currentUser.user_metadata.name.charAt(0)}
      workspaces={[currentWorkspace]}
      currentWorkspaceId={currentWorkspace.id}
      onSwitchWorkspace={() => { }}
      onCreateWorkspace={() => { }}
    >
      {content}

      {/* Demo Walkthrough */}
      {role === UserRole.PROVIDER && !hasSeenAgencyWalkthrough && (
        <DemoWalkthrough
          role={UserRole.PROVIDER}
          onDismiss={() => setHasSeenAgencyWalkthrough(true)}
        />
      )}
      {role === UserRole.RECEIVER && !hasSeenClientWalkthrough && (
        <DemoWalkthrough
          role={UserRole.RECEIVER}
          onDismiss={() => setHasSeenClientWalkthrough(true)}
        />
      )}
    </Layout>
  );
};
